import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

const EMPTY = {
  caller_name: "", caller_phone: "", caller_email: "", direction: "inbound",
  source: "Phone", reason: "", summary: "", has_injury: false,
  already_represented: false, statute_concern: false,
  assessment: "needs_review", assessment_notes: "", status: "new",
};

export default function CallFormDialog({ open, onOpenChange, call, meta, onSaved }) {
  const isEdit = Boolean(call);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(call ? { ...EMPTY, ...call } : EMPTY);
  }, [open, call]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.caller_name.trim() && !form.caller_phone.trim()) {
      toast.error("Caller name or phone is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        caller_name: form.caller_name, caller_phone: form.caller_phone,
        caller_email: form.caller_email, direction: form.direction, source: form.source,
        reason: form.reason, summary: form.summary, has_injury: form.has_injury,
        already_represented: form.already_represented, statute_concern: form.statute_concern,
        assessment: form.assessment, assessment_notes: form.assessment_notes,
        status: form.status,
      };
      if (isEdit) {
        await api.put(`/calls/${call.id}`, payload);
        toast.success("Call updated");
      } else {
        await api.post("/calls", payload);
        toast.success("Call logged");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save call");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="call-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">{isEdit ? "Edit call log" : "Log a call"}</DialogTitle>
          <DialogDescription className="sr-only">Record caller details and a viability assessment.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Caller name</label>
              <input data-testid="call-name-input" value={form.caller_name} onChange={(e) => set("caller_name", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="call-phone-input" value={form.caller_phone} onChange={(e) => set("caller_phone", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Email</label>
              <input data-testid="call-email-input" type="email" value={form.caller_email} onChange={(e) => set("caller_email", e.target.value)} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Direction</label>
                <select data-testid="call-direction-select" value={form.direction} onChange={(e) => set("direction", e.target.value)} className={inputCls}>
                  {(meta.directions || ["inbound", "outbound"]).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Source</label>
                <select data-testid="call-source-select" value={form.source} onChange={(e) => set("source", e.target.value)} className={inputCls}>
                  {(meta.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>Reason for call</label>
            <input data-testid="call-reason-input" value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. Rear-ended at a red light" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Call summary</label>
            <textarea data-testid="call-summary-input" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={3} className={inputCls} />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Viability assessment</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.has_injury} onChange={(e) => set("has_injury", e.target.checked)} data-testid="call-injury-check" className="accent-[#002FA7]" /> Has injury
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.already_represented} onChange={(e) => set("already_represented", e.target.checked)} data-testid="call-represented-check" className="accent-[#002FA7]" /> Already represented
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.statute_concern} onChange={(e) => set("statute_concern", e.target.checked)} data-testid="call-statute-check" className="accent-[#002FA7]" /> Statute of limitations concern
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Assessment</label>
                <select data-testid="call-assessment-select" value={form.assessment} onChange={(e) => set("assessment", e.target.value)} className={inputCls}>
                  {(meta.assessments || []).map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select data-testid="call-status-select" value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                  {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Assessment notes</label>
              <textarea data-testid="call-assessment-notes-input" value={form.assessment_notes} onChange={(e) => set("assessment_notes", e.target.value)} rows={2} className={inputCls} />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="call-save-button"
              className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save changes" : "Log call"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
